import type { ReactElement } from "react";
import { useCallback, useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Box, Flex, Stack } from "@similarweb/ui-components/foundations";

import { FilterCategoryChipList } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterCategoryChipList/FilterCategoryChipList";
import { FilterSearchBox } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterSearchBox/FilterSearchBox";
import FiltersListLoader from "app/pages/sales-intelligence/common-components/filters-panel/components/FiltersListLoader/FiltersListLoader";
import { FilterPanelItem } from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/FilterPanelItem/FilterPanelItem";
import { useCategorizedFiltersViewModel } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useCategorizedFiltersViewModel";
import { useFiltersManager } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useFiltersManager";
import { useLocalFilterListSearch } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useLocalFilterListSearch";
import { useLocalVisibleFilters } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useLocalVisibleFilters";
import type { FilterCategory } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/category";
import type {
    FilterInstanceType,
    IFilterInstance,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { SelectedCriteriaCompaniesCount } from "app/pages/sales-intelligence/modules/leads-search/components/SelectedCriteriaCompaniesCount";
import { isCountriesWorldwide } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { SelectedFiltersSidebar } from "app/pages/sales-intelligence/modules/leads-search-filters/components/selected-filters-sidebar/SelectedFiltersSidebar";
import { VisitsFromWorldwideProvider } from "app/pages/sales-intelligence/modules/leads-search-filters/context/VisitsFromWorldwideContext";
import { useLeadsPanelDraft } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useLeadsPanelDraft";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";
import { getVisitsFromPanelInitialCountries } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/leadsPanelFilterProductDefault";
import type { TeamFilters } from "app/pages/sales-intelligence/modules/teams/types/teams.types";

import { TeamFiltersHeader } from "./TeamFiltersHeader";
import { TeamFiltersListPanel } from "./TeamFiltersListPanel";
import { useTeamFiltersConfig } from "./useTeamFiltersConfig";

type TeamFiltersBodyProps = {
    draftFilters: TeamFilters;
    onFiltersUpdate: (next: TeamFilters) => void;
    startScrolledPastPermanentFilters?: boolean;
};

export const TeamFiltersBody = ({
    draftFilters,
    onFiltersUpdate,
    startScrolledPastPermanentFilters = false,
}: TeamFiltersBodyProps) => {
    const [selectedCategory, setSelectedCategory] = useState<FilterCategory>("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedFilterKey, setExpandedFilterKey] = useState<string | null>(null);

    const { filtersList, filtersSearchConfigList, categoriesList, status } = useTeamFiltersConfig();

    const isReady = status === "ready";

    const { searchResults, isSearchActive } = useLocalFilterListSearch({
        filtersConfig: filtersList,
        searchConfigMaps: filtersSearchConfigList,
        searchQuery,
    });

    const { filterList: visibleFilters, categoriesWithHiddenFilters } = useLocalVisibleFilters({
        filtersConfig: filtersList,
        selectedCategory,
        searchMatchedFilters: searchResults?.matchedFilters,
        isSearchActive,
    });

    const [panelValue, handlePanelUpdate] = useLeadsPanelDraft(draftFilters, onFiltersUpdate);

    // The International Visits filter is gated on the leads "Visits From" being worldwide. That state
    // normally lives in the global leads Redux slice (the opportunity-list panel); here it's driven
    // by the team's draft, so derive it locally (same predicate the selector uses) and inject it via
    // context (ADR-0005).
    const isVisitsFromWorldwide = useMemo(() => {
        // Match VisitsFromFilterModel.fromApiValue: an unset team draft still means Worldwide.
        const countries = panelValue.countries ?? getVisitsFromPanelInitialCountries();
        return isCountriesWorldwide(countries);
    }, [panelValue]);

    const filtersManager = useFiltersManager<LeadsSearchAllFiltersTypes>({
        filtersValue: panelValue,
        filtersConfig: visibleFilters as unknown as readonly IFilterInstance<LeadsSearchAllFiltersTypes>[],
        onFiltersUpdate: handlePanelUpdate,
    });

    const { permanentFilters, groups, categoriesWithCounts } = useCategorizedFiltersViewModel({
        visibleFilters: filtersManager.getAll(),
        fullFiltersConfig: filtersList as unknown as readonly IFilterInstance<LeadsSearchAllFiltersTypes>[],
        categories: categoriesList,
        categoriesWithHiddenFilters,
        isSearchActive,
    });

    const renderFilterCard = useCallback(
        (config: IFilterInstance<LeadsSearchAllFiltersTypes>): ReactElement => {
            const key = String(config.key);
            return (
                <FilterPanelItem<LeadsSearchAllFiltersTypes>
                    key={key}
                    isExpanded={expandedFilterKey === key}
                    filterConfig={config as unknown as FilterInstanceType<LeadsSearchAllFiltersTypes, unknown, unknown>}
                    filterDtoValue={filtersManager.getFilterDtoValue(config.key)}
                    onFilterUpdate={filtersManager.updateFilter}
                    onToggleExpand={() => setExpandedFilterKey((prev) => (prev === key ? null : key))}
                    isShowClearButton={filtersManager.isFilterDirty(config.key)}
                    isDisabled={filtersManager.isFilterDisabled(config.key)}
                    onClear={() => filtersManager.resetFilter(config.key)}
                />
            );
        },
        [expandedFilterKey, filtersManager],
    );

    return (
        <VisitsFromWorldwideProvider value={isVisitsFromWorldwide}>
            <Stack flexShrink={0}>
                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    gap="16px"
                    borderBottom={`1px solid ${colorsPalettes.carbon["100"]}`}
                >
                    <TeamFiltersHeader />
                    <FilterSearchBox onChange={setSearchQuery} />
                </Flex>

                {isReady ? (
                    <Flex
                        alignItems="center"
                        flexWrap="wrap"
                        gap="8px"
                        data-automation="team-filters-chips"
                    >
                        <FilterCategoryChipList
                            categoryList={categoriesWithCounts}
                            selectedCategoryId={selectedCategory}
                            onClick={setSelectedCategory}
                        />
                    </Flex>
                ) : null}
            </Stack>

            <Stack
                flex="1"
                minHeight="0"
            >
                {isReady ? (
                    <Flex
                        flex="1"
                        minHeight="0"
                        alignItems="stretch"
                        margin="0 16px 0 0"
                    >
                        <TeamFiltersListPanel
                            selectedCategory={selectedCategory}
                            startScrolledPastPermanentFilters={startScrolledPastPermanentFilters}
                            permanentFilters={permanentFilters}
                            groups={groups}
                            renderFilterCard={renderFilterCard}
                        />

                        <SelectedFiltersSidebar
                            panelValue={panelValue}
                            onPanelValueUpdate={handlePanelUpdate}
                            filtersList={filtersList}
                            dataAutomationPrefix="team-filters"
                            renderCompaniesCount={({ panelValue: value, isEnabled, dataAutomationPrefix }) => (
                                <SelectedCriteriaCompaniesCount
                                    panelValue={value}
                                    isEnabled={isEnabled}
                                    dataAutomationPrefix={dataAutomationPrefix}
                                    companiesCountTranslationKey={
                                        leadsSearchFiltersI18nKeys.selectedFiltersCompaniesCount
                                    }
                                />
                            )}
                        />
                    </Flex>
                ) : (
                    <Box padding="16px">
                        <FiltersListLoader rowNumber={6} />
                    </Box>
                )}
            </Stack>
        </VisitsFromWorldwideProvider>
    );
};
